import React, { useContext, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { notify } from 'components/Notifier';
import { AppContext } from 'context';
import { Client, SortingCenterContractFormType, Stream } from 'types';
import SortingCenterContractForm from 'components/Forms/SortingCenterContractForm';
import { useClientById } from 'hooks';
import Loading from 'components/Loading';
import PageContent from 'components/Generic/PageContent';
import RequiredFieldsLegend from 'components/Forms/RequiredFieldsLegend';
import BottomButtonBar from 'components/Generic/BottomButtonBar';
import { formatEstimatedReceptionBeforePost, formatSortingCenterContractBeforePost } from 'utils/normalizer';
import { postSortingCenterContractAndReception } from 'services/apiOperations';
import { Routes } from 'routes';
import { TAB_PANEL_PARAM } from './ClientShow';

export type CharacterizationFormContext = {
  client?: Client;
  stream?: Stream;
};

const SortingCenterContractCreate: React.FC = () => {
  const formName = 'sortingCenterContractForm';

  const context = useContext(AppContext);
  const history = useHistory();
  const { t } = useTranslation();
  const { t: pt } = useTranslation('translation', { keyPrefix: 'sortingCenterContracts.create' });

  const { clientId } = useParams<{ clientId: string }>();
  const { value: client } = useClientById(clientId);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const backRoute = generatePath(Routes.ClientShow, { clientId: clientId }) + '?' + TAB_PANEL_PARAM + '=contracts';

  const onSubmit: SubmitHandler<SortingCenterContractFormType> = async (
    sortingCenterContract: SortingCenterContractFormType,
  ) => {
    const sortingCenterId = context.getSortingCenter?.()?.['@id'] ?? null;

    if (null === sortingCenterId) {
      notify({
        message: t('sortingCenterContracts.create.error'),
        severity: 'error',
      });
      return;
    }
    setIsSubmitting(true);
    let formattedSortingCenterContract = formatSortingCenterContractBeforePost(sortingCenterContract, sortingCenterId);
    let formattedEstimatedReception = formatEstimatedReceptionBeforePost(sortingCenterContract, sortingCenterId);
    return postSortingCenterContractAndReception(formattedSortingCenterContract, formattedEstimatedReception)
      .then(() => {
        notify({
          message: t('sortingCenterContracts.create.success'),
          severity: 'success',
        });
        history.push(backRoute);
        setIsSubmitting(false);
      })
      .catch(() => {
        notify({
          message: t('errorGeneric'),
          severity: 'error',
        });
        setIsSubmitting(false);
      });
  };

  return (
    <>
      {client ? (
        <>
          <PageContent title={pt('createPage')} backRoute={backRoute} withButtonBar>
            <SortingCenterContractForm onSubmit={onSubmit} formName={formName} client={client} />
            <RequiredFieldsLegend />
          </PageContent>
          <BottomButtonBar
            previousButtonLink={backRoute}
            formName={formName}
            previousStepLabel={pt('previousStepButtonLabel')}
            nextStepLabel={pt('createButtonLabel')}
            disableNext={isSubmitting}
          />
        </>
      ) : (
        <Loading />
      )}
    </>
  );
};
export default SortingCenterContractCreate;
